import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    SampleActionsType,
    useSampleSelector
} from '../../redux_toolkit/Sample/SampleSlice'
import {
    capitalizeFirstLetter,
    isPageReachBottom,
    onSetSampleDetailsBreadcrumb
} from '../../helpers/Util'
import SampleThunks from '../../redux_toolkit/Sample/SampleThunks'
import { GET_SAMPLE_LIST } from '../../redux_toolkit/@types/thunk.types'
import StyleImageCardView from '../../common/StyleImageCardView'
import SampleTableView from './SampleTableView'
import { SET_SAMPLE_LIST_PAGE_ACTIVE_TAB } from '../../redux_toolkit/@types/action.types'
import { closeLoader, openLoader, useLoader } from '../../redux_toolkit/Loader'
import EmptyDashboard from '../../common/EmptyDashboard'
import { addMixpanelTrackingEvent } from '../../services/CommonService'
import {
    setDashboardViewType,
    useDashboardViewSelector
} from '../../redux_toolkit/Dashboard/DashboardViewSlice'
import { ReactComponent as ListViewUnfillIcon } from '../../../assets/icons/list-view-unfill.svg'
import { ReactComponent as SelectedListViewIcon } from '../../../assets/icons/list-view-filled.svg'
import { ReactComponent as SelectedCardViewIcon } from '../../../assets/icons/grid-view-active.svg'
import { ReactComponent as CollectionUnFillIcon } from '../../../assets/icons/collection-unfill.svg'

const SampleList = ({ total, dateRange }) => {
    const sampleStore = useSampleSelector()
    const dispatch = useDispatch()
    const ActiveStatus = {
        all: 'ALL',
        running: 'PENDING',
        pending: 'INITIALIZED',
        completed: 'COMPLETED',
        cancelled: 'CANCELLED'
    }
    const sampleStoreRef = useRef({})
    const dateRangeRef = useRef(dateRange)
    const { viewType } = useDashboardViewSelector()
    const [showSearch, setShowSearch] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const navigate = useNavigate()
    const location = useLocation()
    const activeStatusParam = new URLSearchParams(location.search).get('status')
    const globalLoader = useLoader()
    const isLoading = useRef(null)
    const setIsDataLoading = (data) => {
        isLoading.current = data
    }

    useEffect(() => {
        setIsDataLoading(globalLoader)
    }, [globalLoader])

    useEffect(() => {
        if (!sampleStore.activeTab) {
            handleChangeActiveStatus(
                ActiveStatus[activeStatusParam?.toLowerCase()] ||
                    ActiveStatus.all
            )
        }
        document.addEventListener('scroll', handleScroll)
        return () => {
            document.removeEventListener('scroll', handleScroll)
            dispatch({
                type: SampleActionsType[SET_SAMPLE_LIST_PAGE_ACTIVE_TAB],
                payload: ''
            })
        }
    }, [])

    useEffect(() => {
        sampleStoreRef.current = sampleStore
    }, [sampleStore.samples])

    useEffect(() => {
        dateRangeRef.current = dateRange
        const currentTab =
            sampleStoreRef.current?.activeTab ||
            ActiveStatus[activeStatusParam?.toLowerCase()] ||
            ActiveStatus.all
        dispatch(
            SampleThunks[GET_SAMPLE_LIST](
                generateParams(0, currentTab),
                currentTab,
                dateRange
            )
        )
    }, [dateRange?.type, dateRange?.fromDate, dateRange?.toDate])

    // useEffect(() => {
    //     searchRef.current.focus()
    // }, [showSearch])

    const handleChangeActiveStatus = (activeStatus) => {
        window.scrollTo(0, 0)
        dispatch(
            SampleThunks[GET_SAMPLE_LIST](
                generateParams(0, activeStatus),
                activeStatus,
                dateRange
            )
        )

        if (activeStatus === 'ALL') {
            navigate('/dashboard?active=SAMPLE&status=ALL')
        } else if (activeStatus === 'PENDING') {
            navigate('/dashboard?active=SAMPLE&status=RUNNING')
        } else if (activeStatus === 'INITIALIZED') {
            navigate('/dashboard?active=SAMPLE&status=PENDING')
        } else {
            navigate(`/dashboard?active=SAMPLE&status=${activeStatus}`)
        }
        const requestBody = {
            requestTab: 'SAMPLE',
            status: activeStatus
        }
        addMixpanelTrackingEvent(requestBody, 'BUYER_DASHBOARD_CHANGE_TAB')
    }

    const generateParams = (page = 0, activeStatus, search = '') => {
        const statusMap = {
            [ActiveStatus.running]: activeStatus,
            [ActiveStatus.pending]: activeStatus,
            [ActiveStatus.completed]: `${activeStatus}`,
            [ActiveStatus.cancelled]: 'INITIALIZED&approvalStatus=CANCELLED'
        }
        // ALL tab fetches without status filter
        if (activeStatus === ActiveStatus.all) {
            return `?size=15&page=${page}&sort=id,desc&search=${search}`
        }
        const status = `?size=15&page=${page}&sort=id,desc&search=${search}${
            statusMap[activeStatus] ? `&status=${statusMap[activeStatus]}` : ''
        }`

        return status
    }

    // const handleSearch = (e) => {
    //     const { value } = e.target
    //     setSearchValue(value)
    //     const activeStatus = sampleStore.activeTab
    //     dispatch(
    //         SampleThunks[GET_SAMPLE_LIST](
    //             generateParams(0, activeStatus, value),
    //             activeStatus
    //         )
    //     )
    // }

    const handleScroll = () => {
        if (isPageReachBottom()) {
            const { totalElements, currentPage, data } =
                sampleStoreRef.current.samples
            const activeTab = sampleStoreRef.current.activeTab
            if (
                totalElements > 0 &&
                totalElements !== data.length &&
                !isLoading.current
            ) {
                dispatch(
                    SampleThunks[GET_SAMPLE_LIST](
                        generateParams(currentPage + 1, activeTab),
                        activeTab,
                        dateRangeRef.current
                    )
                )
            }
        }
    }

    return (
        <>
            <div>
                <div className='flex items-center justify-between'>
                    <div className='quote-sample-state-tab'>
                        <div
                            onClick={() =>
                                handleChangeActiveStatus(ActiveStatus.all)
                            }
                            className={`single-tab ${
                                sampleStore.activeTab === ActiveStatus.all
                                    ? 'active'
                                    : ''
                            }`}
                        >
                            <h4 className='regular-14'>
                                All (
                                {(total?.sample?.RUNNING ?? 0) +
                                    (total?.sample?.PENDING ?? 0) +
                                    (total?.sample?.COMPLETED ?? 0) +
                                    (total?.sample?.CANCELLED ?? 0)}
                                )
                            </h4>
                        </div>
                        <div
                            onClick={() =>
                                handleChangeActiveStatus(ActiveStatus.running)
                            }
                            className={`single-tab ${
                                sampleStore.activeTab === ActiveStatus.running
                                    ? 'active'
                                    : ''
                            }`}
                        >
                            <h4 className='regular-14'>
                                Running ({total?.sample?.RUNNING})
                            </h4>
                        </div>
                        <div
                            onClick={() =>
                                handleChangeActiveStatus(ActiveStatus.pending)
                            }
                            className={`single-tab ${
                                sampleStore.activeTab === ActiveStatus.pending
                                    ? 'active'
                                    : ''
                            }`}
                        >
                            <h4 className='regular-14'>
                                Pending ({total?.sample?.PENDING})
                            </h4>
                        </div>
                        <div
                            onClick={() =>
                                handleChangeActiveStatus(ActiveStatus.completed)
                            }
                            className={`single-tab ${
                                sampleStore.activeTab === ActiveStatus.completed
                                    ? 'active'
                                    : ''
                            }`}
                        >
                            <h4 className='regular-14'>
                                {capitalizeFirstLetter(ActiveStatus.completed)}{' '}
                                ({total?.sample?.COMPLETED})
                            </h4>
                        </div>
                        <div
                            onClick={() =>
                                handleChangeActiveStatus(ActiveStatus.cancelled)
                            }
                            className={`single-tab ${
                                sampleStore.activeTab === ActiveStatus.cancelled
                                    ? 'active'
                                    : ''
                            }`}
                        >
                            <h4 className='regular-14'>
                                {capitalizeFirstLetter(ActiveStatus.cancelled)}{' '}
                                ({total?.sample?.CANCELLED})
                            </h4>
                        </div>
                    </div>

                    <div className='order-view-toggle flex items-center gap-1'>
                        <button
                            className='button !p-0 min-w-[36px] min-h-[36px] flex items-center justify-center'
                            onClick={() =>
                                dispatch(setDashboardViewType('CARD_VIEW'))
                            }
                            title='Card view'
                        >
                            {viewType === 'CARD_VIEW' ? (
                                <SelectedCardViewIcon className='!w-[36px] !h-[36px]' />
                            ) : (
                                <CollectionUnFillIcon className='!w-[20px] !h-[20px]' />
                            )}
                        </button>
                        <button
                            className='button !p-0 min-w-[36px] min-h-[36px] flex items-center justify-center'
                            onClick={() =>
                                dispatch(setDashboardViewType('TABLE_VIEW'))
                            }
                            title='Table view'
                        >
                            {viewType === 'TABLE_VIEW' ? (
                                <SelectedListViewIcon className='!w-[36px] !h-[36px]' />
                            ) : (
                                <ListViewUnfillIcon className='!w-[20px] !h-[20px]' />
                            )}
                        </button>
                    </div>
                </div>

                {viewType === 'TABLE_VIEW' ? (
                    <SampleTableView
                        sampleStore={sampleStore}
                        activeTab={sampleStore.activeTab}
                    />
                ) : (
                    sampleStore?.samples?.data &&
                    sampleStore?.samples?.data?.length > 0 && (
                        <div className='py-4 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4'>
                            {sampleStore?.samples?.data?.map((sample) => {
                                return (
                                    <StyleImageCardView
                                        key={sample.id}
                                        navigationUrl={() => {
                                            onSetSampleDetailsBreadcrumb(
                                                sample,
                                                activeStatusParam,
                                                navigate
                                            )
                                            addMixpanelTrackingEvent(
                                                {},
                                                'BUYER_SAMPLE_ON_CLICK'
                                            )
                                        }}
                                        productFeatureImageList={
                                            sample.productDocuments
                                        }
                                        titleName={sample.title}
                                        referenceNumber={sample.refNo}
                                        noOfDesign={sample.noOfDesign}
                                        status={sample.approvalStatus}
                                    />
                                )
                            })}
                        </div>
                    )
                )}
                {sampleStore?.samples?.data &&
                    sampleStore?.samples?.data?.length === 0 && (
                        <EmptyDashboard requesType='sample' />
                    )}
            </div>
        </>
    )
}

export default SampleList
